<script setup lang="ts" generic="T">
import {
  inject,
  onBeforeUnmount,
  onMounted,
  provide,
  ref,
  watch,
  type ComponentPublicInstance,
} from 'vue';
import { sectionItemIdKey, sectionNavRegistryKey, type SectionNavItemTag } from './internal';

const props = withDefaults(
  defineProps<{
    id: string;
    meta: T;
    /** What to render as — a tag name or a component. Must have a single element root
     *  (the registry needs a real element to observe + scroll to). Defaults to <div>. */
    is?: SectionNavItemTag;
  }>(),
  { is: 'div' }
);

// Slot receives whether this item is the active one, so the content can react
// (e.g. expand, raise opacity) without reaching into the registry itself.
defineSlots<{ default(props: { active: boolean }): unknown }>();

const registry = inject(sectionNavRegistryKey);
if (!registry) throw new Error('[nx-ui] SectionNavItem must be used inside SectionNavRoot');

// A `ref` on <component :is> yields the DOM node for a tag, or the component
// instance for a component — unwrap to the root element in the latter case.
const root = ref<HTMLElement | ComponentPublicInstance | null>(null);
function resolveEl(): HTMLElement | null {
  const r = root.value;
  if (r instanceof HTMLElement) return r;
  const dom = (r as ComponentPublicInstance | null)?.$el;
  return dom instanceof HTMLElement ? dom : null;
}

const idRef = ref(props.id);
watch(
  () => props.id,
  (v) => (idRef.value = v)
);
provide(sectionItemIdKey, idRef);

let unregister: (() => void) | undefined;
function reregister() {
  const el = resolveEl();
  if (!el) return;
  // The registry upserts by id, so re-registering swaps the entry in place without
  // touching active state — no pre-unregister (that path clears the active id).
  unregister = registry!.register({ id: props.id, el, meta: props.meta });
}

onMounted(reregister);
// Re-register whenever id or meta changes so the registry's projection stays current.
watch(() => ({ id: props.id, meta: props.meta }), reregister);
onBeforeUnmount(() => unregister?.());
</script>

<template>
  <component :is="is" :id="id" ref="root" class="nx-section-nav-item">
    <slot :active="registry.activeId.value === props.id" />
  </component>
</template>

<style scoped>
/* Headroom for the default scrollIntoView({block:'start'}): without it a
   clicked section sits flush against the scrollport's top edge (under sticky
   headers, visually "barely there"). scroll-margin also applies inside nested
   scrollports. Matches the scroll-spy's activation line (upper ~18%) so a
   clicked section is also the one the spy would pick. Override per screen via
   the custom property. */
.nx-section-nav-item {
  /* stylelint-disable-next-line declaration-property-value-disallowed-list -- public layout integration hook has no generated equivalent */
  scroll-margin-top: var(--nx-section-scroll-margin-top, 14vh);
}
</style>
